import React from 'react';

import {Anchor, Icon, IconButton, Text} from '@albert-io/atomic';
import type {IconName} from '@fortawesome/fontawesome-common-types';
import classnames from 'classnames';

import {BOOK_TRAINING_LINK, HELP_CENTER_URL, LEARN_ABOUT_LINKS} from './gettingStartedResources';
import {GettingStartedItem} from './cards/GettingStartedItem';
import {GettingStartedVideo} from './cards/GettingStartedVideo';

interface Props {
  onDismiss: () => void;
  /** Whether the rail is open. Drives the slide/collapse animation — the panel stays mounted. */
  open: boolean;
  /**
   * Narrow screens can't fit the rail beside the content, so it renders as a right-aligned panel over a
   * dimming bg covering the content pane.
   */
  overlay?: boolean;
}

export const GetStartedModule = ({onDismiss, open, overlay}: Props) => {
  const transition = 'transition-all duration-[250ms] ease-out';
  // Shared panel shell; the two breakpoints differ only in positioning/animation.
  const asideBase =
    'flex h-full w-[420px] flex-col gap-x3 overflow-y-auto border-l border-border-primary background-primary p-x4';

  // The panel stays mounted so it can animate closed; mark it inert while closed so its links and
  // close button stay out of the tab order and off the a11y tree. Set via ref because `inert` isn't
  // in this React version's JSX attribute types.
  const setInert = (el: HTMLElement | null) => {
    if (el) {
      el.inert = !open;
    }
  };

  const content = (
    <>
      <div className='gap-x1 flex items-center'>
        <Icon
          className='content-primary'
          icon={['fak', 'regular-square-list-circle-question' as IconName]}
        />
        <Text className='grow'>Helpful Links</Text>
        <IconButton
          color='secondary'
          icon='times'
          iconStyle='regular'
          label='Close helpful links'
          onClick={onDismiss}
          tooltip='Close'
          variant='text'
        />
      </div>

      <GettingStartedVideo />

      <GettingStartedItem
        chipClassName={BOOK_TRAINING_LINK.chipClassName}
        href={BOOK_TRAINING_LINK.href}
        icon={BOOK_TRAINING_LINK.icon}
        label={BOOK_TRAINING_LINK.label}
      />

      <div className='gap-x1-half flex flex-col'>
        <Text>Learn about...</Text>
        <ul className='m-x0 gap-x1-half p-x0 flex list-none flex-col'>
          {LEARN_ABOUT_LINKS.map((link) => (
            <li className='flex' key={link.href}>
              <GettingStartedItem
                chipClassName={link.chipClassName}
                href={link.href}
                icon={link.icon}
                label={link.label}
              />
            </li>
          ))}
        </ul>
      </div>

      <Text className='text-center' color='secondary' size='s'>
        Find more in our{' '}
        <Anchor
          as='a'
          href={HELP_CENTER_URL}
          inheritColor
          rel='noopener noreferrer'
          target='_blank'
          underlined
        >
          Help Center
        </Anchor>
      </Text>
    </>
  );

  // Narrow screens: a right-aligned panel that slides in over a dimming bg covering the content pane.
  // Clicking the bg — or Enter/Space/Escape when it's focused — dismisses, same as the X.
  if (overlay) {
    return (
      <>
        <div
          aria-label='Close helpful links'
          className={classnames(
            'absolute top-x0 left-x0 z-20 h-full w-full overlay-35',
            transition,
            {
              'cursor-pointer opacity-100': open,
              'pointer-events-none opacity-0': !open
            }
          )}
          onClick={onDismiss}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
              onDismiss();
            }
          }}
          role='button'
          tabIndex={open ? 0 : -1}
        />
        <aside
          aria-label='Getting started'
          className={classnames(
            asideBase,
            'absolute top-x0 right-x0 z-30 max-w-full shadow-l4',
            transition,
            {
              'translate-x-full': !open
            }
          )}
          ref={setInert}
        >
          {content}
        </aside>
      </>
    );
  }

  // Wide screens: the rail sits beside the content. Animate its width so the content reclaims the
  // space smoothly; the inner panel keeps a fixed width and is clipped during the transition.
  return (
    <div
      className={classnames('shrink-0 overflow-hidden', transition, {
        'w-[420px]': open,
        'w-x0': !open
      })}
    >
      <aside aria-label='Getting started' className={asideBase} ref={setInert}>
        {content}
      </aside>
    </div>
  );
};
